import { useContext, useEffect, useMemo, useState } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type SortingState,
  type ColumnFiltersState,
  type RowSelectionState,
  ColumnDef,
} from "@tanstack/react-table"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { ArrowUpDown, Search, ChevronDown, ChevronLeft, Trash2, ChevronRight } from "lucide-react"
import useTranslator from "@pma/hooks/use-translator"
import { DataTableColumnHeader } from "@components/common/data-table/data-table-column-header"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { DictType } from "@pma/types"
import {
  buildBoQHierarchicalData,
  cn,
  exportToExcel,
  getAllItemChildrenIds,
} from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { Checkbox } from "@components/common/checkbox"
import CreateMainBoQ from "./create-main-boq"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

interface Props {
  boqId: string
  data: any[]
  revalidate: CallableFunction
}

export default function EstimateBoQTable({ boqId, data, revalidate }: Props) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(""))
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [isMobile, setIsMobile] = useState(false)

  const { userRoleDetails } = useContext(UserRoleContext)

  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const hierarchicalData = useMemo(() => buildBoQHierarchicalData(data), [data])

  const isRowVisible = (item: DictType): boolean => {
    if (item.level === 0) return true
    const codeParts = item?.number.split(".")
    for (let i = 1; i <= item.level; i++) {
      const parentCode = codeParts.slice(0, i).join(".")
      if (!expandedRows.has(parentCode)) return false
    }
    return true
  }

  const visibleData = useMemo(() => {
    return hierarchicalData.filter(isRowVisible)
  }, [hierarchicalData, expandedRows])

  const toggleExpanded = (number: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(number)) {
      newExpanded.delete(number)
      hierarchicalData.forEach((item) => {
        if (item?.number?.startsWith(number + ".")) {
          newExpanded.delete(item.number)
        }
      })
    } else {
      newExpanded.add(number)
    }
    setExpandedRows(newExpanded)
  }

  const handleRowSelection = (rowId: string, isSelected: boolean) => {
    const newSelection = { ...rowSelection }
    const item = hierarchicalData.find((item) => item.id === rowId)

    if (!item) return

    if (isSelected) {
      newSelection[rowId] = true
      hierarchicalData.forEach((child) => {
        if (child?.number.startsWith(item?.number + ".")) {
          newSelection[child.id] = true
        }
      })
    } else {
      delete newSelection[rowId]
      hierarchicalData.forEach((child) => {
        if (child?.number.startsWith(item?.number + ".")) {
          delete newSelection[child.id]
        }
      })
      if (item.parentCode) {
        const siblings = hierarchicalData.filter(
          (sibling) => sibling.parentCode === item.parentCode,
        )
        const hasSelectedSibling = siblings.some((sibling) => newSelection[sibling.id])
        if (!hasSelectedSibling) {
          const parent = hierarchicalData.find((parent) => parent?.number === item.parentCode)
          if (parent) delete newSelection[parent.id]
        }
      }
    }
    setRowSelection(newSelection)
  }

  const allSelectedIds = useMemo(
    () => getAllItemChildrenIds(rowSelection, hierarchicalData),
    [rowSelection, data],
  )

  const selectedCount = allSelectedIds.size
  const visibleSelectedCount = visibleData.filter((item) =>
    allSelectedIds.has(String(item.id)),
  ).length

  const columns = useMemo(
    (): ColumnDef<any>[] => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            className='mx-2'
            onCheckedChange={(value) => {
              if (value) {
                const newSelection = { ...rowSelection }
                visibleData.forEach((row) => {
                  newSelection[row.id] = true
                  hierarchicalData.forEach((child) => {
                    if (child?.number.startsWith(row?.number + ".")) {
                      newSelection[child.id] = true
                    }
                  })
                })
                setRowSelection(newSelection)
              } else {
                setRowSelection({})
              }
            }}
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={!!rowSelection[row.original.id]}
            onCheckedChange={(value) => handleRowSelection(row.original.id, !!value)}
            aria-label='Select row'
            className='mx-2'
          />
        ),
        size: isMobile ? 30 : 40,
      },
      {
        accessorKey: "number",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Number')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemNumber = row?.original?.number
          const isExpanded = expandedRows.has(itemNumber)
          const parentCode = String(row.original.parentCode || "1")
          const indentationPx = parentCode.split(".").length * (isMobile ? 16 : 30)

          return (
            <div
              className={cn("flex min-w-0 items-center gap-1")}
              style={{
                paddingRight: isHebrew ? `${indentationPx}px` : undefined,
                paddingLeft: !isHebrew ? `${indentationPx}px` : undefined,
              }}
            >
              {row?.original.hasChildren && (
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-4 w-4 flex-shrink-0 p-0'
                  onClick={() => toggleExpanded(itemNumber)}
                >
                  {isExpanded ? (
                    <ChevronDown className='h-4 w-4' />
                  ) : (
                    <ChevronLeft className='h-4 w-4' />
                  )}
                </Button>
              )}
              <span className='truncate font-mono text-xs sm:text-sm'>{itemNumber}</span>
            </div>
          )
        },
        enableSorting: false,
        size: isMobile ? 80 : 120,
      },
      {
        accessorKey: "description",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Description')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemDescription = row?.original?.description
          return (
            <div className='line-clamp-2 text-right text-xs leading-tight sm:text-sm'>
              {itemDescription}
            </div>
          )
        },
        size: isMobile ? 120 : 300,
        enableSorting: false,
      },
      {
        accessorKey: "units",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Unit')} className='' />,
        cell: ({ row }: any) => {
          const itemUnit = row?.original?.units
          return <div className='text-center text-xs sm:text-sm'>{itemUnit}</div>
        },
        size: isMobile ? 50 : 60,
        enableSorting: false,
      },
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Amount')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemAmount = row?.original?.amount
          return <div className='text-center font-mono text-xs sm:text-sm'>{itemAmount || ""}</div>
        },
        size: isMobile ? 45 : 50,
        enableSorting: false,
      },
      {
        accessorKey: "price",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Price')} className='' />
        ),
        cell: ({ row }: any) => {
          const itemPrice = row?.original?.price
          return (
            <div className='text-center font-mono text-xs sm:text-sm'>
              {itemPrice ? formatCurrency(itemPrice, isHebrew) : ""}
            </div>
          )
        },
        size: isMobile ? 45 : 50,
        enableSorting: false,
      },
      {
        accessorKey: "sum",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Sum')} className='' />,
        cell: ({ row }: any) => {
          const itemSum = row?.original?.sum
          return (
            <div className='text-center text-xs font-bold sm:text-sm'>
              {formatCurrency(itemSum, isHebrew)}
            </div>
          )
        },
        size: isMobile ? 45 : 50,
        enableSorting: false,
      },
    ],
    [expandedRows, rowSelection, visibleData, hierarchicalData, isMobile],
  )

  const table = useReactTable({
    data: visibleData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
  })

  const sortedData = useMemo(
    () =>
      data.sort((a, b) => {
        const aParts = a.number.split(".").map(Number)
        const bParts = b.number.split(".").map(Number)
        for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          if (aParts[i] === undefined) return -1
          if (bParts[i] === undefined) return 1
          if (aParts[i] < bParts[i]) return -1
          if (aParts[i] > bParts[i]) return 1
        }
        return 0
      }),
    [data],
  )

  useEffect(() => {
    setRowSelection({})
  }, [data])

  useEffect(() => {
    const allCodes = new Set(hierarchicalData.map((item) => item.number))
    setExpandedRows(allCodes)
  }, [])

  return (
    <div className='h-full w-full p-2 sm:p-4' dir={isHebrew ? "rtl" : "ltr"}>
      {/* Search and Action Bar */}
      <div className='mb-3 flex flex-col gap-3 sm:mb-4 sm:gap-2'>
        <div className='relative w-full sm:w-[300px]'>
          <Search className='absolute right-2 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            placeholder={translate("Search") + "..."}
            value={globalFilter ?? ""}
            onChange={(event) => setGlobalFilter(String(event.target.value))}
            className='w-full pr-8 text-sm'
          />
        </div>

        {/* Selection and Control Buttons */}
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end'>
          {selectedCount > 0 && Object.keys(rowSelection).length > 0 && (
            <div className='flex flex-col gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm'>
              <span className='text-xs text-primary/70 sm:text-sm'>
                {selectedCount} {translate("items selected")} ({visibleSelectedCount}{" "}
                {translate("visible")})
              </span>
              {(userRoleDetails?.supervisor || userRoleDetails?.fin_manager) ? (
                <div className='flex flex-col flex-wrap gap-2 sm:flex-row'>
                  <CreateMainBoQ
                    revalidate={revalidate}
                    estimateItems={Object.keys(rowSelection)}
                    estimateBoQId={boqId}
                  />
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setRowSelection({})}
                    className='border-red-200 text-xs text-red-700 hover:bg-red-100'
                  >
                    <Trash2 className='mr-1 h-3 w-3' />
                    {translate("Clear Selection")}
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          <div className='flex flex-wrap gap-2'>
            <Button
              variant='outline'
              className='h-8 border-primary text-xs text-primary sm:h-9 sm:text-sm'
              size='sm'
              onClick={() => exportToExcel(hierarchicalData, "estimate")}
            >
              {translate("Export All to Excel")}
            </Button>
            <Button
              variant='outline'
              size='sm'
              className='h-8 text-xs sm:h-9 sm:text-sm'
              onClick={() => {
                const allCodes = new Set(hierarchicalData.map((item) => item.number))
                const isAllExpanded = hierarchicalData.every((item) =>
                  expandedRows.has(item.number),
                )

                if (isAllExpanded) {
                  setExpandedRows(new Set(`${sortedData[0]?.number}`))
                } else {
                  setExpandedRows(allCodes)
                }
              }}
            >
              {hierarchicalData.every((item) => expandedRows.has(item.number)) ? (
                <>
                  <ChevronRight className='mr-1 h-3 w-3' />
                  <span className='hidden sm:inline'>{translate("Collapse All")}</span>
                  <span className='sm:hidden'>{translate("Collapse")}</span>
                </>
              ) : (
                <>
                  <ChevronDown className='mr-1 h-3 w-3' />
                  <span className='hidden sm:inline'>{translate("Expand All")}</span>
                  <span className='sm:hidden'>{translate("Expand")}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className='max-h-[calc(100vh-300px)] max-w-xs overflow-x-auto overflow-y-auto rounded-md border sm:max-h-[60vh] md:max-w-xl lg:max-w-full'>
        <Table className=''>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className='sticky top-0 border-b bg-gray-50'>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className='whitespace-nowrap border-l border-gray-200 px-1.5 py-2 text-center text-xs text-gray-900 first:border-l-0 sm:px-2 sm:py-3 sm:text-sm'
                    style={{ width: header.getSize() }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? "flex cursor-pointer select-none items-center justify-center gap-1"
                            : "flex items-center justify-center"
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <ArrowUpDown className='h-2.5 w-2.5 sm:h-3 sm:w-3' />
                        )}
                      </div>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row, index) => {
              const rowData = row.original
              const isParent = rowData.hasChildren

              return (
                <TableRow
                  key={row.id}
                  className={`border-b transition-colors hover:bg-gray-50 ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-25"
                  } ${isParent ? "font-medium" : ""}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className='border-l border-gray-200 px-1.5 py-1.5 text-xs first:border-l-0 sm:px-2 sm:py-2 sm:text-sm'
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Footer Info */}
      <div className='flex items-center justify-end space-x-2 py-2 text-xs sm:py-4 sm:text-sm'>
        <div className='text-muted-foreground'>
          {translate("Showing")} {table.getFilteredRowModel().rows.length} {translate("of")}{" "}
          {hierarchicalData.length} {translate("rows")}
        </div>
      </div>
    </div>
  )
}
